import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { useSettings, SettingsState } from '../../hooks/useSettings';
import { useI18n } from '../../i18n/LanguageContext';

const telemedItOverrides: Record<string, string> = {
    'Video Provider': 'Provider video',
    'API Key': 'Chiave API',
    Region: 'Regione',
    'Meeting URL Pattern': 'Pattern URL riunione',
    'Max Participants': 'Partecipanti massimi',
    'Auto-Join Waiting Room': 'Ingresso automatico in sala d’attesa',
    'Consent Text': 'Testo consenso',
    'Quality/Bandwidth': 'Qualità/Banda',
};

function createTelemedTranslator(language: string, tr: (value: string) => string) {
    if (language !== 'it') return tr;
    return (phrase: string) => {
        const normalized = phrase.trim();
        if (!normalized) return phrase;
        const globalTranslation = tr(normalized);
        if (globalTranslation !== normalized) return phrase.replace(normalized, globalTranslation);
        const local = telemedItOverrides[normalized];
        return local ? phrase.replace(normalized, local) : phrase;
    };
}

export default function SettingsTelemed() {
    const navigate = useNavigate();
    const { settings, setSettings, load, save, loading, error } = useSettings();
    const { language, tr, t: translateKey } = useI18n();
    const tt = createTelemedTranslator(language, tr);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        load();
    }, [load]);

    async function handleSave() {
        setSaving(true);
        setSuccess(null);
        try {
            await save(settings);
            setSuccess(tt('Saved successfully'));
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    }

    function updateField(updater: (prev: SettingsState['telemed']) => SettingsState['telemed']) {
        setSettings((prev) => ({ ...prev, telemed: updater(prev.telemed) }));
    }

    if (loading) return <div className="p-6">{translateKey('common.loading', 'Loading...')}</div>;
    if (error) return <div className="p-6 text-rose-600">{tt('Error')}: {error}</div>;

    const telemedSettings = settings.telemed;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/settings')} className="p-2 hover:bg-slate-100 rounded-full">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-semibold text-ink">{tt('Telemedicine Settings')}</h1>
                        <p className="text-slate-500 text-sm">{tt('Configure video visits and waiting rooms.')}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {success && <span className="text-emerald-600 text-sm">{success}</span>}
                    <button
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-ink text-white shadow-soft disabled:opacity-60"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        <Save size={16} /> {saving ? tr('Saving...') : translateKey('common.save', 'Save')}
                    </button>
                </div>
            </div>

            <div className="card p-6 max-w-3xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <Input label={tt('Video Provider')} value={telemedSettings.provider} onChange={(v) => updateField((prev) => ({ ...prev, provider: v }))} />
                    <Input label={tt('API Key')} value={telemedSettings.apiKey} onChange={(v) => updateField((prev) => ({ ...prev, apiKey: v }))} />
                    <Input label={tt('Region')} value={telemedSettings.region} onChange={(v) => updateField((prev) => ({ ...prev, region: v }))} />
                    <Input
                        label={tt('Meeting URL Pattern')}
                        value={telemedSettings.meetingUrlPattern}
                        onChange={(v) => updateField((prev) => ({ ...prev, meetingUrlPattern: v }))}
                    />
                    <Input
                        label={tt('Max Participants')}
                        type="number"
                        value={String(telemedSettings.waitingRoomMax)}
                        onChange={(v) => updateField((prev) => ({ ...prev, waitingRoomMax: Number(v) }))}
                    />
                    <div className="pt-6">
                        <Toggle
                            label={tt('Auto-Join Waiting Room')}
                            value={telemedSettings.waitingRoomAutoJoin}
                            onChange={(v) => updateField((prev) => ({ ...prev, waitingRoomAutoJoin: v }))}
                        />
                    </div>
                    <Input
                        label={tt('Consent Text')}
                        value={telemedSettings.consentText}
                        onChange={(v) => updateField((prev) => ({ ...prev, consentText: v }))}
                    />
                    <Input label={tt('Quality/Bandwidth')} value={telemedSettings.quality} onChange={(v) => updateField((prev) => ({ ...prev, quality: v }))} />
                    <div className="pt-6">
                        <Toggle
                            label={tt('Group Video Appointments')}
                            value={telemedSettings.groupVideoEnabled}
                            onChange={(v) => updateField((prev) => ({ ...prev, groupVideoEnabled: v }))}
                        />
                    </div>
                    <Input
                        label={tt('Default Max Participants')}
                        type="number"
                        value={String(telemedSettings.defaultMaxParticipants)}
                        onChange={(v) => updateField((prev) => ({ ...prev, defaultMaxParticipants: Math.max(2, Number(v) || 2) }))}
                    />
                    <TextArea
                        label={tt('Video Consultation Support')}
                        value={telemedSettings.consultationSupportText}
                        onChange={(v) => updateField((prev) => ({ ...prev, consultationSupportText: v }))}
                    />
                    <TextArea
                        label={tt('Video FAQ / Troubleshooting')}
                        value={telemedSettings.troubleshootingFaq}
                        onChange={(v) => updateField((prev) => ({ ...prev, troubleshootingFaq: v }))}
                    />
                </div>
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
    return (
        <label className="text-sm space-y-1 block">
            <span className="text-slate-500">{label}</span>
            <input
                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
                value={value}
                type={type}
                onChange={(e) => onChange(e.target.value)}
            />
        </label>
    );
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
    return (
        <label className="text-sm space-y-1 block md:col-span-2">
            <span className="text-slate-500">{label}</span>
            <textarea
                className="min-h-[96px] w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </label>
    );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
    return (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
            <span className="text-slate-700 font-medium">{label}</span>
        </label>
    );
}
